// Public task-template catalog — same chrome as /tasks (TasksPage) with
// browse-specific SEO. Template cards open /tasks/template/{slug}; "Schedule it"
// opens SetupTaskFromTemplateModal (see TasksPage + TemplatePinCard).
import React, { useMemo } from 'react';
import { View } from 'react-native';
import { useTranslation } from '@/hooks/useTranslation';
import MetaHead from '@/components/MetaHead';
import { TasksPage } from '@/app/(app)/tasks/index';
import { useTaskTemplatesStore } from '@/store';
import { taskTemplateApi as taskTemplateApiSsr } from '@/lib/taskTemplateApi';

let _staticTemplatesCache = null;
async function getStaticTemplates() {
  if (_staticTemplatesCache) return _staticTemplatesCache;
  try {
    const data = await taskTemplateApiSsr.listTemplates();
    _staticTemplatesCache = data.templates || [];
  } catch {
    _staticTemplatesCache = [];
  }
  return _staticTemplatesCache;
}

if (typeof window === 'undefined') {
  getStaticTemplates().catch(() => {});
}

function TasksBrowseRoute() {
  const { t } = useTranslation();
  const templates = useTaskTemplatesStore((s) => s.templates);

  const seoStructuredData = useMemo(() => {
    const cacheList = Array.isArray(_staticTemplatesCache) ? _staticTemplatesCache : [];
    const liveList = templates || [];
    const sourceList = liveList.length ? liveList : cacheList;
    const itemList = sourceList.slice(0, 100).map((tpl, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      url: `https://gab.ai/tasks/template/${tpl.slug}`,
      name: tpl?.meta?.title || tpl?.name,
    }));
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          name: 'Scheduled AI Task Templates',
          description:
            'Pre-built recipes for scheduled AI tasks — daily news briefings, market summaries, content workflows, monitoring alerts, and more. Customize any template and let Gab AI run it on your schedule.',
          url: 'https://gab.ai/tasks/browse',
          isPartOf: { '@type': 'WebSite', name: 'Gab AI', url: 'https://gab.ai' },
          inLanguage: 'en-US',
          mainEntity: { '@type': 'ItemList', itemListElement: itemList },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://gab.ai' },
            { '@type': 'ListItem', position: 2, name: 'Task Templates', item: 'https://gab.ai/tasks/browse' },
          ],
        },
      ],
    };
  }, [templates]);

  return (
    <View style={{ flex: 1 }}>
      <MetaHead
        title={t('pages.tasks.browseTitle') || 'Scheduled AI Task Templates'}
        description={
          t('pages.tasks.browseDescription')
          || 'Browse pre-built recipes for scheduled AI tasks: daily news briefings, market summaries, content workflows, monitoring alerts, and more. Customize any template and let Gab AI run it on your schedule.'
        }
        canonical="/tasks/browse"
        keywords="AI task templates, scheduled AI, AI automation, recurring AI tasks, AI workflows, daily AI digest, AI scheduler"
        structuredData={seoStructuredData}
      />
      <TasksPage browseMode />
    </View>
  );
}

export default React.memo(TasksBrowseRoute);
